import React, { useEffect, useState } from 'react';
import { FormBuilderService } from '../../api/FormBuilderService';
import { IAttachedJob } from '../../types/questionnaire';
import './AttachedJobsList.css';
import Spinner from '../Spinner';
import Icon from '../ui/Icon/Icon';

interface AttachedJobsListProps {
  questionnaireId: string;
  initiallyExpanded?: boolean;
  // VI-579 follow-up (Bug 3): fired with the true server count immediately after the fetch
  // resolves. Lets the parent (View / Reactivate modal → Table → Home) overwrite the cached
  // count on the questionnaires table when it disagrees with reality (e.g. stale sessionStorage
  // after a job-status change or a mutation from the Jobs-tab drawer).
  onCountResolved?: (count: number) => void;
}

const formatLocation = (job: IAttachedJob): string => {
  const primary = job.locations?.[0];
  if (!primary) return '';
  const { city, region } = primary;
  return [city, region].filter(Boolean).join(', ');
};

const submissionLabel = (count: number): string =>
  count <= 1 ? 'Submission' : 'Submissions';

const AttachedJobsList: React.FC<AttachedJobsListProps> = ({
  questionnaireId,
  initiallyExpanded = false,
  onCountResolved,
}) => {
  const [isExpanded, setIsExpanded] = useState(initiallyExpanded);
  const [jobs, setJobs] = useState<IAttachedJob[] | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (jobs !== null) return;

    let cancelled = false;
    const fetchJobs = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const result = await FormBuilderService.getAttachedJobsWithSubmissions(questionnaireId);
        if (!cancelled) {
          setJobs(result);
          // VI-579 follow-up (Bug 3): teach the parent (and through it the table's cache) the
          // true count we just observed. Only fire on success so a fetch failure doesn't
          // clobber the cached value with a misleading 0.
          onCountResolved?.(result.length);
        }
      } catch (err: unknown) {
        if (!cancelled) {
          const message = err instanceof Error ? err.message : 'Failed to load attached jobs';
          setError(message);
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    fetchJobs();
    return () => {
      cancelled = true;
    };
    // onCountResolved intentionally NOT included: callers pass a fresh function each render,
    // which would re-trigger the fetch unnecessarily. The fetch's only meaningful inputs are
    // `jobs` (null → trigger) and `questionnaireId`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobs, questionnaireId]);

  const isInitialLoading = jobs === null && isLoading;

  return (
    <div className="attached-jobs">
      <button
        type="button"
        className="attached-jobs__toggle"
        onClick={() => setIsExpanded((prev) => !prev)}
        aria-expanded={isExpanded}
        disabled={isInitialLoading && !error}
      >
        <span>
          {isInitialLoading ? (
            <>
              <Spinner size="sm" color="primary" /> <span className="ml-2">Loading Attached Jobs...</span>
            </>
          ) : (
            <>Show {jobs?.length ?? 0} Attached Jobs</>
          )}
        </span>
        <Icon icon={`fal ${isExpanded ? 'fa-minus' : 'fa-plus'}`} ariaHidden />
      </button>

      {isExpanded && (
        <div className="attached-jobs__panel">
          {isLoading && (
            <div className="attached-jobs__state">
              <i className="fa fa-spinner fa-spin mr-2"></i>Loading attached jobs...
            </div>
          )}

          {error && !isLoading && (
            <div className="attached-jobs__state attached-jobs__state--error">{error}</div>
          )}

          {!isLoading && !error && jobs && jobs.length === 0 && (
            <div className="attached-jobs__state">No active jobs attached.</div>
          )}

          {!isLoading && !error && jobs && jobs.length > 0 && (
            <ul className="attached-jobs__list">
              {jobs.map((job) => (
                <li key={job.jobId} className="attached-jobs__row">
                  <div className="attached-jobs__job-info">
                    <div className="attached-jobs__title">{job.title}</div>
                    {formatLocation(job) && (
                      <div className="attached-jobs__location">{formatLocation(job)}</div>
                    )}
                  </div>
                  <div className="attached-jobs__count">
                    <div className="attached-jobs__count-number">{job.submissionCount}</div>
                    <div className="attached-jobs__count-label">
                      {submissionLabel(job.submissionCount)}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};

export default AttachedJobsList;
